<script setup lang="ts">
import { computed, toRef, type StyleValue } from 'vue'
import { getImageGridRows } from '#shared/lib/images/get-image-grid-rows'
import type { ImageGridRow, ImageInfo } from '#shared/model/image-grid'

const {
  imageInfos,
  doFitNaturalSize = false,
  maxImagesPerRow = 3,
  maxImageArea = Infinity,
} = defineProps<{
  imageInfos: ImageInfo[]
  doFitNaturalSize?: boolean
  maxImagesPerRow?: number
  maxImageArea?: number
}>()

const rows = computed(() =>
  getImageGridRows(
    imageInfos,
    toRef(() => maxImagesPerRow)
  )
)

const naturalWidth = computed(() => {
  const rowWidths = rows.value.map(
    row => row.cells.reduce((acc, cell) => acc + cell.imageInfo.width, 0) + (row.cells.length - 1) * 5
  )

  const maxRowWidth = Math.max(...rowWidths)
  return maxRowWidth
})

const naturalHeight = computed(() => {
  const rowHeights = rows.value.map(row => Math.max(...row.cells.map(cell => cell.imageInfo.height)))
  return rowHeights.reduce((acc, height) => acc + height, 0)
})

const aspectRatio = computed(() => {
  const totalHeight = rows.value.reduce((acc, row) => acc + row.relativeHeight, 0)
  return 1 / totalHeight
})

const rootStyle = computed(() => {
  const style: StyleValue = {
    aspectRatio: aspectRatio.value,
    gridTemplateRows: rows.value.map(row => row.relativeHeight * aspectRatio.value + 'fr').join(' '),
  }

  let maxWidth = Infinity
  let maxHeight = Infinity

  if (doFitNaturalSize) {
    maxWidth = naturalWidth.value
    maxHeight = naturalHeight.value
  }

  if (maxImageArea !== Infinity) {
    const unitArea = aspectRatio.value
    const sizeInUnits = Math.sqrt(maxImageArea / unitArea)

    maxWidth = Math.min(maxWidth, sizeInUnits * aspectRatio.value)
    maxHeight = Math.min(maxHeight, sizeInUnits)
  }

  if (maxWidth !== Infinity) style.maxWidth = `${maxWidth}px`
  if (maxHeight !== Infinity) style.maxHeight = `${maxHeight}px`

  return style
})

const getRowStyle = (row: ImageGridRow) => {
  return { gridTemplateColumns: row.cells.map(cell => cell.relativeWidth + 'fr').join(' ') }
}
</script>

<template>
  <div :class="$style.root" :style="rootStyle">
    <div v-for="(row, rowIndex) in rows" :class="$style.row" :style="getRowStyle(row)" :key="rowIndex">
      <div v-for="cell in row.cells" :class="$style.cell" :key="cell.imageInfo.src">
        <slot :imageInfo="cell.imageInfo" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  display: grid;
  gap: 5px;
}

.row {
  display: grid;
  grid-auto-flow: column;
  gap: 5px;
  min-height: 0;
  min-width: 0;
}

.cell {
  min-height: 0;
  min-width: 0;
  display: flex;
}
</style>
